import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, type AdminMark } from '../../lib/api.ts';

export function AdminMarker() {
  const qc = useQueryClient();
  const marks = useQuery({ queryKey: ['marks'], queryFn: () => api.listMarks() });
  const [editing, setEditing] = useState<AdminMark | null>(null);
  const [showNew, setShowNew] = useState(false);

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-semibold">Merker</h1>
        <button
          className="bg-sky-600 hover:bg-sky-500 px-3 py-2 rounded text-sm font-semibold"
          onClick={() => {
            setShowNew(true);
            setEditing(null);
          }}
        >
          + Nytt merke
        </button>
      </div>

      <p className="text-xs text-slate-500 mb-4">
        Et merke er et fast punkt (stake, øy) eller en bøye med tracker (live posisjon). Merkene
        brukes til å bygge baner.
      </p>

      {(showNew || editing) && (
        <MarkForm
          initial={editing ?? undefined}
          onClose={() => {
            setShowNew(false);
            setEditing(null);
            qc.invalidateQueries({ queryKey: ['marks'] });
          }}
        />
      )}

      {marks.isLoading ? (
        <p className="text-slate-500 text-sm">Laster…</p>
      ) : marks.data && marks.data.length > 0 ? (
        <ul className="divide-y divide-slate-800 border border-slate-800 rounded-xl overflow-hidden">
          {marks.data.map((m) => (
            <li key={m.id} className="p-3 bg-slate-900/30 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate flex items-center gap-2">
                  {m.name}
                  <span className="text-xs bg-slate-800 text-slate-400 px-2 py-0.5 rounded">
                    {m.source === 'tracker' ? '📡 bøye' : '📍 fast'}
                  </span>
                </div>
                <div className="text-xs text-slate-400 truncate">
                  {m.source === 'tracker'
                    ? `${m.trackerLabel || m.trackerImei || 'ingen tracker'}${
                        m.trackerLastLat != null
                          ? ` • ${m.trackerLastLat.toFixed(4)}, ${m.trackerLastLon!.toFixed(4)}`
                          : ' • ingen posisjon enda'
                      }`
                    : m.lat != null
                      ? `${m.lat.toFixed(5)}, ${m.lon!.toFixed(5)}`
                      : 'ingen posisjon'}
                </div>
              </div>
              <button
                onClick={() => {
                  setEditing(m);
                  setShowNew(false);
                }}
                className="text-xs text-sky-400"
              >
                Rediger
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <div className="text-center text-slate-500 text-sm p-6 border border-slate-800 rounded-xl">
          Ingen merker enda.
        </div>
      )}
    </div>
  );
}

function MarkForm({ initial, onClose }: { initial?: AdminMark; onClose: () => void }) {
  const qc = useQueryClient();
  const trackers = useQuery({ queryKey: ['trackers'], queryFn: () => api.listTrackers() });
  const [name, setName] = useState(initial?.name ?? '');
  const [source, setSource] = useState<'fixed' | 'tracker'>(initial?.source ?? 'fixed');
  const [trackerId, setTrackerId] = useState(initial?.trackerId ?? '');
  const [lat, setLat] = useState(initial?.lat != null ? String(initial.lat) : '');
  const [lon, setLon] = useState(initial?.lon != null ? String(initial.lon) : '');
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const payload = {
        name,
        source,
        trackerId: source === 'tracker' ? trackerId || null : null,
        lat: source === 'fixed' ? (lat ? Number(lat) : null) : null,
        lon: source === 'fixed' ? (lon ? Number(lon) : null) : null,
      };
      if (initial) return api.updateMark(initial.id, payload);
      return api.createMark(payload);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marks'] });
      onClose();
    },
    onError: (e: ApiError) => setErr(e.message),
  });

  const remove = useMutation({
    mutationFn: async () => initial && api.deleteMark(initial.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['marks'] });
      onClose();
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        save.mutate();
      }}
      className="mb-4 bg-slate-900/60 border border-slate-800 rounded-xl p-4 space-y-3"
    >
      <h2 className="font-semibold">{initial ? 'Rediger merke' : 'Nytt merke'}</h2>
      {err && (
        <div className="text-red-300 bg-red-900/30 border border-red-800 rounded p-2 text-sm">{err}</div>
      )}
      <div>
        <label className="block text-xs text-slate-400 mb-1">Navn</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="f.eks. Bøye 1 / Komitébåt / Pinne"
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
        />
      </div>
      <div>
        <label className="block text-xs text-slate-400 mb-1">Type</label>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setSource('fixed')}
            className={`flex-1 py-2 rounded-lg text-sm ${
              source === 'fixed' ? 'bg-sky-600 text-white' : 'bg-slate-800 text-slate-300'
            }`}
          >
            📍 Fast punkt
          </button>
          <button
            type="button"
            onClick={() => setSource('tracker')}
            className={`flex-1 py-2 rounded-lg text-sm ${
              source === 'tracker' ? 'bg-sky-600 text-white' : 'bg-slate-800 text-slate-300'
            }`}
          >
            📡 Bøye m/ tracker
          </button>
        </div>
      </div>
      {source === 'tracker' ? (
        <div>
          <label className="block text-xs text-slate-400 mb-1">Tracker</label>
          <select
            value={trackerId}
            onChange={(e) => setTrackerId(e.target.value)}
            required
            className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg"
          >
            <option value="">— Velg tracker —</option>
            {(trackers.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.label || t.imei}
                {t.boatSail ? ` (nå på båt ${t.boatSail})` : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-slate-500 mt-1">
            Posisjonen følger trackeren. En tracker som brukes som merke løsnes fra båt.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <label className="block text-xs text-slate-400 mb-1">Breddegrad</label>
            <input
              value={lat}
              onChange={(e) => setLat(e.target.value)}
              inputMode="decimal"
              placeholder="58.3400"
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg font-mono"
            />
          </div>
          <div>
            <label className="block text-xs text-slate-400 mb-1">Lengdegrad</label>
            <input
              value={lon}
              onChange={(e) => setLon(e.target.value)}
              inputMode="decimal"
              placeholder="8.5900"
              className="w-full px-3 py-2 bg-slate-950 border border-slate-700 rounded-lg font-mono"
            />
          </div>
        </div>
      )}
      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          disabled={save.isPending}
          className="flex-1 bg-sky-600 hover:bg-sky-500 py-3 rounded font-semibold disabled:opacity-60"
        >
          {save.isPending ? 'Lagrer…' : 'Lagre'}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-3 bg-slate-800 hover:bg-slate-700 rounded font-semibold"
        >
          Avbryt
        </button>
      </div>
      {initial && (
        <button
          type="button"
          onClick={() => {
            if (confirm(`Arkivere merket ${initial.name}?`)) remove.mutate();
          }}
          className="w-full text-xs text-red-400 py-2"
        >
          Arkiver merke
        </button>
      )}
    </form>
  );
}
